import React, { FC, useMemo } from 'react';
import Select, {
  StylesConfig,
  components,
  CSSObjectWithLabel,
  GroupBase,
  Props,
  DropdownIndicatorProps,
  NoticeProps,
} from 'react-select';
import { AsyncPaginate, AsyncPaginateProps } from 'react-select-async-paginate';
import AsyncSelect, { AsyncProps } from 'react-select/async';
import AsyncCreatableSelect from 'react-select/async-creatable';
import clsx from 'clsx';
import './SearchSelect.scss';

interface SearchSelectProps<T = unknown> extends Omit<Props<T, boolean, GroupBase<T>>, 'isMulti'> {
  placeHolder?: string;
  name?: string;
  inputClassName?: string;
  placeholder?: string;
  showBlue?: boolean;
  showGreen?: boolean;
  showRed?: boolean;
  isUpdating?: boolean;
  label?: string;
  selectRef?: any;
  parent?: HTMLElement | null;
  startIcon?: React.ReactNode;
  error?: string;
  isSmall?: boolean;
  isMulti?: boolean;
  onCreateNew?: (inputValue: string) => void;
}
interface AsyncSelectProps<T = unknown>
  extends AsyncProps<T, false, GroupBase<T>> {
  onChange?: (e: T | null) => void;
  placeHolder?: string;
  name?: string;
  inputClassName?: string;
  placeholder?: string;
  showBlue?: boolean;
  showGreen?: boolean;
  showRed?: boolean;
  isUpdating?: boolean;
  label?: string;
  onCreateNew?: (inputValue: string) => void;
}
interface AsyncPageSelectProps<T = unknown>
  extends AsyncPaginateProps<T, GroupBase<T>, false, false> {
  onChange?: (e: T | null) => void;
  placeHolder?: string;
  name?: string;
  inputClassName?: string;
  placeholder?: string;
  showBlue?: boolean;
  showGreen?: boolean;
  showRed?: boolean;
  isUpdating?: boolean;
  label?: string;
  isSmall?: boolean;
  onCreateNew?: (inputValue: string) => void;
}
interface AsyncCreatableSelectProps<T = unknown>
  extends AsyncProps<T, false, GroupBase<T>> {
  onChange?: (e: T | null) => void;
  placeHolder?: string;
  name?: string;
  inputClassName?: string;
  placeholder?: string;
  showBlue?: boolean;
  showGreen?: boolean;
  showRed?: boolean;
  isUpdating?: boolean;
  label?: string;
  isSmall?: boolean;
  onCreateNew?: (inputValue: string) => void;
}

const sharedStyles: StylesConfig<any, boolean> = {
  control: (provided, state) => ({
    ...provided,
    borderRadius: '0.625rem',
    height: '56px',
    borderColor: state.isFocused ? '#2277db' : '#ccc',
    '&:hover': {
      borderColor: '#2277db',
    },
    boxShadow: state.isFocused ? '0 0 0 1px #2277db' : 'none',
    transition: 'border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
  }),

  placeholder: (
    provided: CSSObjectWithLabel,
    state: DropdownIndicatorProps<unknown, boolean, GroupBase<unknown>>,
  ) => ({
    ...provided,

    transform:
      (state.selectProps.menuIsOpen && !state.hasValue) || state.hasValue
        ? 'translateY(-40%) scale(0.8)'
        : 'none',
    transition: 'all 0.2s cubic-bezier(0.25, 0.1, 0.25, 1)',
    left:
      (state.selectProps.menuIsOpen && !state.hasValue) || state.hasValue
        ? '0px'
        : 'none',
    display: 'flex',
    position: 'absolute',
    pointerEvents: 'none', // Prevents interaction with placeholder
    top:
      (state.selectProps.isMulti && !state.hasValue) || state.hasValue
        ? '4px'
        : '',
  }),

  singleValue: (provided) => ({
    ...provided,
    fontSize: '0.875rem',
    color: '#333',
    marginLeft: '4px',
    transition: 'opacity 0.2s ease-in-out',
  }),
  multiValue: (provided) => ({
    ...provided,
    backgroundColor: 'transparent',
    borderRadius: '0',
    paddingTop: '2px',
    transition: 'opacity 0.2s ease-in-out',
  }),

  multiValueLabel: (provided) => ({
    ...provided,
    fontSize: '0.875rem',
    color: '#333',
  }),

  dropdownIndicator: (provided, state) => ({
    ...provided,
    transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
    transition: 'transform 0.2s ease-in-out',
    color: state.isFocused ? '#2277db' : '#999',
    '&:hover': {
      color: '#2277db',
    },
  }),

  menu: (provided) => ({
    ...provided,
    borderRadius: '0.625rem',
    marginTop: '4px',
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
    overflow: 'hidden',
  }),

  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? '#2277db'
      : state.isFocused
      ? '#e6f0ff'
      : 'white',
    color: state.isSelected ? '#fff' : '#333',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease-in-out',
    '&:active': {
      backgroundColor: '#d0e4ff',
    },
  }),
};

const CustomValueContainer = ({ children, ...props }: any) => {
  const { ValueContainer, Placeholder } = components;
  return (
    <ValueContainer {...props}>
      {props.selectProps.placeholder && (
        <Placeholder {...props} isFocused={props.isFocused}>
          {props.selectProps.placeholder}
        </Placeholder>
      )}
      {React.Children.map(children, (child) =>
        child && child.type !== Placeholder ? child : null,
      )}
    </ValueContainer>
  );
};

const LoadingIndicator = () => (
  <div className="w-4 h-4 border border-dashed rounded-full animate-spinner border-blue-00" />
);

const NoOptionsMessage = <T, IsMulti extends boolean>({
  children,
  innerProps,
  selectProps,
  onCreateNew
}: NoticeProps<T, IsMulti, GroupBase<T>> & { onCreateNew?: (inputValue: string) => void }) => {
  if (!onCreateNew) {
    return <div className="px-4 py-2 text-gray-500">{children}</div>;
  }

  return (
    <div className="px-4 py-2">
      <div className="mb-2 text-gray-500">{children}</div>
      <button
        type="button"
        className="flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-800"
        onClick={() => onCreateNew(selectProps.inputValue)}
      >
        <span>Create new</span>
        <span className="font-bold">"{selectProps.inputValue}"</span>
      </button>
    </div>
  );
};

// === 📚 Components ===

const SelectRootTest = <T,>({
  isMulti,
  selectRef,
  isSmall,
  startIcon,
  parent,
  isUpdating,
  isDisabled,
  onBlur,
  placeholder,
  required,
  error,
  onCreateNew,
  ...props
}: SearchSelectProps<T>) => {
  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    onBlur?.(e);
  };

  const updatedStyles = useMemo<StylesConfig<T, false>>(() => {
    return {
      ...sharedStyles,
      control: (provided: CSSObjectWithLabel) => ({
        ...provided,
        height: isMulti ? '' : isSmall ? '48px' : '56px',
        padding: isMulti ? '8px' : undefined,
        paddingTop: isMulti ? '16px' : undefined,
      }),
    };
  }, [isMulti, isSmall]);

  return (
    <div className="search-select">
      {startIcon && <div className="icon">{startIcon}</div>}
      {isMulti ? (
        <Select<T, true>
          components={{
            ValueContainer: CustomValueContainer,
            LoadingIndicator,
            NoOptionsMessage: (props) => (
              <NoOptionsMessage<T, true>
                {...props}
                onCreateNew={onCreateNew}
              />
            ),
          }}
          styles={updatedStyles}
          {...props}
          classNamePrefix="react-select"
          className={clsx('react-select-container', {
            'react-select-start-icon': !!startIcon,
          })}
          isLoading={isUpdating}
          ref={selectRef}
          menuPortalTarget={parent}
          isDisabled={isDisabled || !navigator.onLine}
          isMulti={true}
          onBlur={handleBlur}
          placeholder={`${placeholder}${required ? '*' : ''}`}
        />
      ) : (
        <Select<T, false>
          components={{
            ValueContainer: CustomValueContainer,
            LoadingIndicator,
            NoOptionsMessage: (props) => (
              <NoOptionsMessage<T, false>
                {...props}
                onCreateNew={onCreateNew}
              />
            ),
          }}
          styles={updatedStyles}
          {...props}
          classNamePrefix="react-select"
          className={clsx('react-select-container', {
            'react-select-start-icon': !!startIcon,
          })}
          isLoading={isUpdating}
          ref={selectRef}
          menuPortalTarget={parent}
          isDisabled={isDisabled || !navigator.onLine}
          isMulti={false}
          onBlur={handleBlur}
          placeholder={`${placeholder}${required ? '*' : ''}`}
        />
      )}
      {error && (
        <p className="mt-1 ml-4 text-xs text-left text-red-00">{error}</p>
      )}
    </div>
  );
};
const SelectAsync = <T,>({ onCreateNew, ...props }: AsyncSelectProps<T>) => (
  <div className="mb-2 search-select">
    <AsyncSelect<T>
      styles={sharedStyles}
      {...props}
      components={{
        ValueContainer: CustomValueContainer,
        LoadingIndicator,
        NoOptionsMessage: (props) => (
          <NoOptionsMessage
            {...props}
            onCreateNew={onCreateNew}
          />
        ),
      }}
      classNamePrefix="select"
    />
  </div>
);

const SelectPaginate = <T,>({
  isSmall,
  isMulti,
  onCreateNew,
  cacheUniqs = [],
  ...props
}: AsyncPageSelectProps<T>) => {
  const updatedStyles = useMemo<StylesConfig<T, false>>(() => {
    return {
      ...sharedStyles,
      control: (provided: CSSObjectWithLabel) => ({
        ...provided,
        height: isMulti ? '' : isSmall ? '48px' : '56px',
        padding: isMulti ? '8px' : undefined,
        paddingTop: isMulti ? '16px' : undefined,
      }),
    };
  }, [isMulti, isSmall]);

  return (
    <div className="mb-2 search-select">
      <AsyncPaginate<T, GroupBase<T>, false, false>
        styles={updatedStyles}
        cacheUniqs={cacheUniqs}
        {...props}
        components={{
          ValueContainer: CustomValueContainer,
          LoadingIndicator,
          NoOptionsMessage: (props) => (
            <NoOptionsMessage
              {...props}
              onCreateNew={onCreateNew}
            />
          ),
        }}
        classNamePrefix="select"
      />
    </div>
  );
};

const SelectAsyncCreatable = <T,>({
  isSmall,
  isMulti,
  onCreateNew,
  ...props
}: AsyncCreatableSelectProps<T>) => {
  const updatedStyles = useMemo<StylesConfig<T, false>>(() => {
    return {
      ...sharedStyles,
      control: (provided: CSSObjectWithLabel) => ({
        ...provided,
        height: isMulti ? '' : isSmall ? '48px' : '56px',
        padding: isMulti ? '8px' : undefined,
        paddingTop: isMulti ? '16px' : undefined,
      }),
    };
  }, [isMulti, isSmall]);

  return (
    <div className="mb-2 search-select">
      <AsyncCreatableSelect<T>
        styles={updatedStyles}
        {...props}
        components={{
          ValueContainer: CustomValueContainer,
          LoadingIndicator,
          NoOptionsMessage: (props) => (
            <NoOptionsMessage
              {...props}
              onCreateNew={onCreateNew}
            />
          ),
        }}
        classNamePrefix="select"
      />
    </div>
  );
};

export { SelectRootTest, SelectAsync, SelectPaginate, SelectAsyncCreatable };
